import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@supabase/auth-helpers-react";

export type Category = "power_bi" | "sql" | "python";

export interface NextStep {
  category: Category | null;
  moduleId: string | null;
  moduleTitle: string | null;
  status: "not_started" | "in_progress" | "all_completed";
  completedCount: number;
  totalCount: number;
  hasAnyProgress: boolean;
}

/**
 * Determina onde o aluno parou considerando todas as trilhas.
 * Prioriza: 1) módulo in_progress mais recente 2) primeiro módulo não completo
 */
export function useAcademyProgress() {
  const user = useUser();

  return useQuery<NextStep>({
    queryKey: ["academy-progress", user?.id],
    queryFn: async () => {
      const empty: NextStep = {
        category: null,
        moduleId: null,
        moduleTitle: null,
        status: "not_started",
        completedCount: 0,
        totalCount: 0,
        hasAnyProgress: false,
      };
      if (!user?.id) return empty;

      const { data: modules } = await supabase
        .from("study_trail_modules")
        .select("id, title, category, order_index")
        .in("category", ["power_bi", "sql", "python"])
        .eq("is_active", true)
        .order("category")
        .order("order_index");

      if (!modules || modules.length === 0) return empty;

      const { data: progress } = await supabase
        .from("user_module_progress")
        .select("module_id, status, updated_at")
        .eq("user_id", user.id);

      const progressMap = new Map(
        (progress || []).map((p: any) => [p.module_id, p])
      );

      const completedCount = modules.filter(
        (m) => progressMap.get(m.id)?.status === "completed"
      ).length;

      // 1. Módulo in_progress mais recente
      const inProgress = modules
        .map((m) => ({ m, p: progressMap.get(m.id) }))
        .filter((x) => x.p?.status === "in_progress")
        .sort(
          (a, b) =>
            new Date(b.p!.updated_at).getTime() - new Date(a.p!.updated_at).getTime()
        );

      if (inProgress.length > 0) {
        const { m } = inProgress[0];
        return {
          category: m.category as Category,
          moduleId: m.id,
          moduleTitle: m.title,
          status: "in_progress",
          completedCount,
          totalCount: modules.length,
          hasAnyProgress: true,
        };
      }

      // 2. Primeiro módulo sem completar (sequência natural)
      const nextIncomplete = modules.find(
        (m) => progressMap.get(m.id)?.status !== "completed"
      );

      if (nextIncomplete) {
        return {
          category: nextIncomplete.category as Category,
          moduleId: nextIncomplete.id,
          moduleTitle: nextIncomplete.title,
          status: completedCount > 0 ? "in_progress" : "not_started",
          completedCount,
          totalCount: modules.length,
          hasAnyProgress: completedCount > 0,
        };
      }

      return {
        ...empty,
        status: "all_completed",
        completedCount,
        totalCount: modules.length,
        hasAnyProgress: true,
      };
    },
    enabled: !!user?.id,
  });
}
